import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Palette, Pipette, Copy, Check, Info, X } from 'lucide-react';
import {
  parseColor, rgbToHsl, hslToRgb, toHex, toHex8, toRgbString, toHslString,
  contrastRatio, wcagVerdict, WCAG_THRESHOLDS,
} from './colorMath';
import { resolveTokens, resolveToken, nearestToken } from './cssTokens';
import { buildHoneycomb, buildGrayscale, honeycombBounds } from './honeycomb';
import StuiSelect from '../StuiSelect';

/* ─────────────────────────────────────────────────────────────
   STUI Color Picker — the Office Colors dialog.

   Standard is a fixed hex grid plus a neutral strip; Custom is a
   hue x saturation field with a separate luminance bar and a
   Color model selector, so a value can be typed as RGB, HSL or hex
   and seen land on the field. HSL is the state of record because
   that is the model the two controls are built from.

   It is a PURE colour picker. It knows nothing about themes or
   palettes — a caller that wants to offer its own colours renders
   them itself and opens this to change one. The only bridge back
   to the application's own colours is the footer readout, which
   names the nearest --color-* token and can return var(--token)
   in place of a frozen hex.
─────────────────────────────────────────────────────────────── */

const HEX_SIZE = 11;
const RINGS = 5;
const RECENTS_KEY = 'pmd-stui-color-recents';
const RECENTS_MAX = 8;
const NEAR_THRESHOLD = 40;          // RGB distance under which a token is "close"
const FALLBACK = { r: 26, g: 85, b: 104, a: 1 };

const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));
const clamp01 = n => clamp(n, 0, 1);

const tokenFromValue = value => {
  const m = typeof value === 'string' && value.trim().match(/^var\(\s*(--[\w-]+)\s*\)$/);
  return m ? m[1] : null;
};

const readRecents = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(RECENTS_KEY));
    return Array.isArray(parsed) ? parsed.filter(v => typeof v === 'string').slice(0, RECENTS_MAX) : [];
  } catch {
    return [];
  }
};

const writeRecents = list => {
  try { localStorage.setItem(RECENTS_KEY, JSON.stringify(list.slice(0, RECENTS_MAX))); } catch { /* private mode */ }
};

/* Pointer drag over a track, reported as 0-1 fractions of its box. */
const useTrackDrag = onMove => {
  const ref = useRef(null);
  const report = useCallback(e => {
    const el = ref.current;
    if (!el) return;
    const box = el.getBoundingClientRect();
    onMove(clamp01((e.clientX - box.left) / box.width), clamp01((e.clientY - box.top) / box.height));
  }, [onMove]);
  return {
    ref,
    onPointerDown: e => { e.currentTarget.setPointerCapture(e.pointerId); report(e); },
    onPointerMove: e => { if (e.currentTarget.hasPointerCapture(e.pointerId)) report(e); },
  };
};

/* ── Standard tab ──────────────────────────────────────────── */

const StandardGrid = ({ selectedHex, onPick }) => {
  const cells = useMemo(() => buildHoneycomb({ rings: RINGS, size: HEX_SIZE }), []);
  const grays = useMemo(() => buildGrayscale(12), []);
  const bounds = useMemo(() => honeycombBounds(cells, HEX_SIZE), [cells]);
  const cellW = HEX_SIZE * 2;
  const cellH = HEX_SIZE * Math.sqrt(3);

  return (
    <>
      <div className="stui-cp-comb" style={{ width: bounds.width, height: bounds.height }}>
        {cells.map(cell => (
          <button
            key={`${cell.q},${cell.r}`}
            type="button"
            className={`stui-cp-hex${cell.hex === selectedHex ? ' selected' : ''}`}
            title={cell.hex}
            onClick={() => onPick(cell.hex)}
            style={{
              left: cell.x - bounds.minX - HEX_SIZE,
              top: cell.y - bounds.minY - cellH / 2,
              width: cellW,
              height: cellH,
              backgroundColor: cell.hex,
            }}
          />
        ))}
      </div>
      <div className="stui-cp-grays">
        {grays.map(gray => (
          <button
            key={gray.hex}
            type="button"
            className={`stui-cp-hex${gray.hex === selectedHex ? ' selected' : ''}`}
            title={gray.hex}
            onClick={() => onPick(gray.hex)}
            style={{ width: cellW, height: cellH, backgroundColor: gray.hex }}
          />
        ))}
      </div>
    </>
  );
};

/* ── Numeric field used by the Custom tab's model inputs ───── */

const NumField = ({ label, value, min, max, suffix, onChange }) => (
  <label className="stui-cp-numrow">
    <span className="stui-cp-numlabel">{label}</span>
    <input
      type="number"
      className="stui-cp-num"
      value={value}
      min={min}
      max={max}
      onChange={e => onChange(clamp(Number(e.target.value), min, max))}
    />
    {suffix && <span className="stui-cp-numsuffix">{suffix}</span>}
  </label>
);

/* ── Modal ─────────────────────────────────────────────────── */

const ColorPickerModal = ({
  isOpen = false,
  title = 'Colors',
  value = '',
  allowAlpha = true,
  nested = false,
  onClose,
  onSelect,
}) => {
  const [tab, setTab] = useState('standard');
  const [hsl, setHsl] = useState({ h: 195, s: 0.6, l: 0.25 });
  const [alpha, setAlpha] = useState(1);
  const [model, setModel] = useState('RGB');
  const [hexText, setHexText] = useState('#1a5568');
  const [outputMode, setOutputMode] = useState('hex');
  const [initial, setInitial] = useState(FALLBACK);
  const [recents, setRecents] = useState([]);
  const [copied, setCopied] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  const rgb = useMemo(() => hslToRgb(hsl), [hsl]);
  const rgba = useMemo(() => ({ ...rgb, a: alpha }), [rgb, alpha]);
  const hex = useMemo(() => toHex(rgb), [rgb]);
  const hexOut = useMemo(() => toHex8(rgba), [rgba]);

  /* Token table is resolved once per open — cheap, and it picks up a theme change. */
  const tokens = useMemo(() => (isOpen ? resolveTokens() : []), [isOpen]);
  const match = useMemo(() => nearestToken(rgba, tokens), [rgba, tokens]);
  const exactToken = match && match.exact ? match.token : null;

  /* Apply an rgba, keeping the current hue when the colour is grey
     (rgbToHsl reports h=0 for greys, which would jerk the field). */
  const applyRgba = useCallback((next, syncHex = true) => {
    if (!next) return;
    setHsl(prev => {
      const conv = rgbToHsl(next);
      return { h: conv.s === 0 ? prev.h : conv.h, s: conv.s, l: conv.l };
    });
    setAlpha(next.a === undefined ? 1 : next.a);
    if (syncHex) setHexText(toHex8(next));
  }, []);

  /* Seed from the incoming value each time the modal opens. */
  useEffect(() => {
    if (!isOpen) return;
    const incomingToken = tokenFromValue(value);
    const raw = incomingToken ? resolveToken(incomingToken) : value;
    const parsed = parseColor(raw) || FALLBACK;
    applyRgba(parsed);
    setInitial(parsed);
    setOutputMode(incomingToken ? 'token' : 'hex');
    setTab('standard');
    setModel('RGB');
    setRecents(readRecents());
    setCopied(false);
    setHelpOpen(false);
  }, [isOpen, value, applyRgba]);

  useEffect(() => {
    if (!isOpen) return undefined;
    /* Stopping propagation matters when stacked: the harmony dialog
       listens on window too, and one Escape must close only the top. */
    const onKey = e => {
      if (e.key !== 'Escape') return;
      if (nested) e.stopImmediatePropagation();
      onClose?.();
    };
    window.addEventListener('keydown', onKey, nested);
    return () => window.removeEventListener('keydown', onKey, nested);
  }, [isOpen, onClose, nested]);

  /* Field is hue across, saturation down; luminance lives on its own bar. */
  const fieldDrag = useTrackDrag(useCallback((x, y) => {
    setHsl(prev => {
      const next = { ...prev, h: x * 360, s: 1 - y };
      setHexText(toHex8({ ...hslToRgb(next), a: alpha }));
      return next;
    });
  }, [alpha]));

  const lumDrag = useTrackDrag(useCallback((x, y) => {
    setHsl(prev => {
      const next = { ...prev, l: 1 - y };
      setHexText(toHex8({ ...hslToRgb(next), a: alpha }));
      return next;
    });
  }, [alpha]));

  const alphaDrag = useTrackDrag(useCallback(x => setAlpha(x), []));

  const nudge = (e, apply) => {
    const step = e.shiftKey ? 5 : 1;
    const dx = e.key === 'ArrowRight' ? step : e.key === 'ArrowLeft' ? -step : 0;
    const dy = e.key === 'ArrowDown' ? step : e.key === 'ArrowUp' ? -step : 0;
    if (!dx && !dy) return;
    e.preventDefault();
    apply(dx, dy);
  };

  const setRgbChannel = (key, n) => {
    const next = { ...rgb, [key]: n, a: alpha };
    applyRgba(next);
  };

  const commitHex = text => {
    setHexText(text);
    const parsed = parseColor(text);
    if (parsed) applyRgba(allowAlpha ? parsed : { ...parsed, a: 1 }, false);
  };

  const pickFlat = nextHex => {
    applyRgba({ ...parseColor(nextHex), a: alpha });
    setOutputMode(mode => (mode === 'token' ? 'hex' : mode));
  };

  const eyeDrop = async () => {
    if (!window.EyeDropper) return;
    try {
      const result = await new window.EyeDropper().open();
      applyRgba({ ...parseColor(result.sRGBHex), a: alpha });
      setOutputMode('hex');
    } catch { /* the user pressed Escape */ }
  };

  const outValue = outputMode === 'token' && exactToken ? `var(${exactToken})`
    : outputMode === 'rgba' ? toRgbString(rgba)
    : hexOut;

  const copyOut = async () => {
    try {
      await navigator.clipboard.writeText(outValue);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    } catch { /* clipboard blocked — the value is on screen */ }
  };

  const confirm = () => {
    const next = [outValue, ...recents.filter(v => v !== outValue)].slice(0, RECENTS_MAX);
    setRecents(next);
    writeRecents(next);
    onSelect?.({
      value: outValue,
      hex: hexOut,
      rgba: toRgbString(rgba),
      hsl: toHslString(rgba),
      alpha,
      token: exactToken,
    });
    onClose?.();
  };

  if (!isOpen) return null;

  const white = { r: 255, g: 255, b: 255, a: 1 };
  const textColor = parseColor(resolveToken('--color-gray-900')) || { r: 17, g: 24, b: 39, a: 1 };

  /* Both readings put the chosen colour behind the text, because that is
     the decision being made: if I fill something with this, what can I
     write on it? The ratio is symmetric, so each number answers the
     reverse question — this colour as text on that background — too. */
  const pairings = [
    { key: 'white', label: 'white text', ink: toRgbString(white), ratio: contrastRatio(rgba, white) },
    { key: 'dark',  label: 'dark text',  ink: toRgbString(textColor), ratio: contrastRatio(rgba, textColor) },
  ];

  /* AA and AAA both PASS — only Fail is a problem, so neither gets a
     cautionary colour. Green and teal read as fine; the label carries
     which tier was met. */
  const badgeFor = verdict => (verdict === 'Fail' ? 'stui-badge-red'
    : verdict === 'AA' ? 'stui-badge-teal' : 'stui-badge-green');

  /* No colour can carry both white and dark text, so one of the two
     readings is always a failure. Showing them as equals therefore made
     every colour in the palette lead with a Fail — including the page
     background, where white-on-white is a true but useless verdict. So
     lead with the winner as advice and demote the loser to a footnote.
     The badge follows whatever the advice claims, never a stricter
     category, so the two can't contradict each other. */
  const [best, other] = pairings[0].ratio >= pairings[1].ratio
    ? [pairings[0], pairings[1]]
    : [pairings[1], pairings[0]];
  const bestSmall = wcagVerdict(best.ratio, 'small');
  const bestLarge = wcagVerdict(best.ratio, 'large');
  const capitalise = s => s.charAt(0).toUpperCase() + s.slice(1);
  const headline = bestSmall !== 'Fail'
    ? { text: `Use ${best.label}`, verdict: bestSmall, scope: 'small text' }
    : bestLarge !== 'Fail'
      ? { text: `${capitalise(best.label)} — large text and UI only`, verdict: bestLarge, scope: 'large text' }
      : { text: 'No text color passes on this', verdict: 'Fail', scope: 'small text' };

  const canEyeDrop = typeof window !== 'undefined' && 'EyeDropper' in window;

  return (
    <div
      className={`stui-modal-overlay${nested ? ' stui-modal-overlay-nested' : ''}`}
      onMouseDown={e => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="stui-modal stui-cp-modal">
        <div className="stui-modal-header">
          <h3><Palette size={18} className="stui-cp-title-icon" />{title}</h3>
          <button className="stui-modal-close" onClick={onClose} title="Close"><X size={20} /></button>
        </div>

        <div className="stui-modal-body stui-cp-body">
          <div className="stui-cp-main">
            <div className="stui-cp-left">
              <div className="stui-cp-tabbar">
                {['standard', 'custom'].map(id => (
                  <button
                    key={id}
                    type="button"
                    className={`stui-cp-tab${tab === id ? ' active' : ''}`}
                    onClick={() => setTab(id)}
                  >
                    {id === 'standard' ? 'Standard' : 'Custom'}
                  </button>
                ))}
              </div>

              <span className="stui-cp-caption">Colors:</span>

              {tab === 'standard' && <StandardGrid selectedHex={hex} onPick={pickFlat} />}

              {tab === 'custom' && (
                <>
                  <div className="stui-cp-custom">
                    <div
                      className="stui-cp-field"
                      tabIndex={0}
                      role="slider"
                      aria-label="Hue and saturation"
                      aria-valuenow={Math.round(hsl.h)}
                      aria-valuemin={0}
                      aria-valuemax={360}
                      aria-valuetext={`hue ${Math.round(hsl.h)} degrees, saturation ${Math.round(hsl.s * 100)}% — ${hex}`}
                      onKeyDown={e => nudge(e, (dx, dy) => setHsl(prev => ({
                        ...prev,
                        h: (prev.h + dx * 2 + 360) % 360,
                        s: clamp01(prev.s - dy * 0.02),
                      })))}
                      {...fieldDrag}
                    >
                      <span
                        className="stui-cp-crosshair"
                        style={{ left: `${(hsl.h / 360) * 100}%`, top: `${(1 - hsl.s) * 100}%` }}
                      />
                    </div>
                    <div
                      className="stui-cp-lum"
                      tabIndex={0}
                      role="slider"
                      aria-label="Luminance"
                      aria-valuenow={Math.round(hsl.l * 100)}
                      aria-valuemin={0}
                      aria-valuemax={100}
                      style={{ backgroundImage: `linear-gradient(to top, #000, hsl(${hsl.h}, ${hsl.s * 100}%, 50%), #fff)` }}
                      onKeyDown={e => nudge(e, (dx, dy) => setHsl(prev => ({ ...prev, l: clamp01(prev.l - dy * 0.02) })))}
                      {...lumDrag}
                    >
                      <span className="stui-cp-lum-marker" style={{ top: `${(1 - hsl.l) * 100}%` }} />
                    </div>
                  </div>

                  <label className="stui-cp-modelrow">
                    <span className="stui-cp-numlabel">Color model:</span>
                    <StuiSelect className="stui-select stui-cp-model" value={model} onChange={e => setModel(e.target.value)}>
                      <option value="RGB">RGB</option>
                      <option value="HSL">HSL</option>
                      <option value="Hex">Hex</option>
                    </StuiSelect>
                    {canEyeDrop && (
                      <button type="button" className="stui-cp-icon-btn" onClick={eyeDrop} title="Pick a color from the screen">
                        <Pipette size={14} />
                      </button>
                    )}
                  </label>

                  {model === 'RGB' && (
                    <>
                      <NumField label="Red:"   value={rgb.r} min={0} max={255} onChange={n => setRgbChannel('r', n)} />
                      <NumField label="Green:" value={rgb.g} min={0} max={255} onChange={n => setRgbChannel('g', n)} />
                      <NumField label="Blue:"  value={rgb.b} min={0} max={255} onChange={n => setRgbChannel('b', n)} />
                    </>
                  )}

                  {model === 'HSL' && (
                    <>
                      <NumField label="Hue:" value={Math.round(hsl.h)} min={0} max={360} suffix="°"
                        onChange={n => { setHsl(p => ({ ...p, h: n })); }} />
                      <NumField label="Sat:" value={Math.round(hsl.s * 100)} min={0} max={100} suffix="%"
                        onChange={n => { setHsl(p => ({ ...p, s: n / 100 })); }} />
                      <NumField label="Lum:" value={Math.round(hsl.l * 100)} min={0} max={100} suffix="%"
                        onChange={n => { setHsl(p => ({ ...p, l: n / 100 })); }} />
                    </>
                  )}

                  {model === 'Hex' && (
                    <label className="stui-cp-numrow">
                      <span className="stui-cp-numlabel">Hex:</span>
                      <input
                        className="stui-cp-hexinput"
                        value={hexText}
                        onChange={e => commitHex(e.target.value)}
                        onBlur={() => setHexText(hexOut)}
                        placeholder="#1a5568, rgb(), hsl(), teal…"
                        spellCheck={false}
                      />
                    </label>
                  )}

                  {allowAlpha && (
                    <label className="stui-cp-numrow">
                      <span className="stui-cp-numlabel">Opacity:</span>
                      <div className="stui-cp-alpha stui-cp-checker" {...alphaDrag}>
                        <span className="stui-cp-alpha-fill" style={{ backgroundImage: `linear-gradient(to right, rgba(0,0,0,0), ${hex})` }} />
                        <span className="stui-cp-alpha-thumb" style={{ left: `${alpha * 100}%`, backgroundColor: hex }} />
                      </div>
                      <span className="stui-cp-numsuffix">{Math.round(alpha * 100)}%</span>
                    </label>
                  )}
                </>
              )}
            </div>

            <div className="stui-cp-rail">
              <div className="stui-cp-compare">
                <span className="stui-cp-comparelabel">New</span>
                <div className="stui-cp-swatch stui-cp-checker">
                  <span style={{ background: toRgbString(rgba) }} />
                </div>
                <div className="stui-cp-swatch stui-cp-checker">
                  <span style={{ background: toRgbString(initial) }} />
                </div>
                <span className="stui-cp-comparelabel">Current</span>
              </div>
            </div>
          </div>

          <div className="stui-cp-status">
            <div className="stui-cp-statusrow">
              <span className="stui-cp-statuslabel">Token</span>
              {!match && <span className="stui-text-italic">No --color-* tokens found</span>}
              {match && match.exact && <code className="stui-cp-tokenname">= {match.token}</code>}
              {match && !match.exact && match.distance <= NEAR_THRESHOLD && (
                <code className="stui-cp-tokenname near">≈ {match.token} (Δ {Math.round(match.distance)})</code>
              )}
              {match && !match.exact && match.distance > NEAR_THRESHOLD && (
                <span className="stui-text-italic">No close token</span>
              )}
            </div>
            <div className="stui-cp-statusrow stui-cp-contrastrow">
              <span className="stui-cp-statuslabel">Contrast</span>
              <div className="stui-cp-pairs">
                <div className="stui-cp-pair">
                  <span
                    className="stui-cp-pairsample"
                    style={{ background: toRgbString(rgba), color: best.ink }}
                  >
                    Aa
                  </span>
                  <span className="stui-cp-advice">{headline.text}</span>
                  <span className="stui-cp-ratio">{best.ratio.toFixed(2)}:1</span>
                  <span
                    className={`stui-badge ${badgeFor(headline.verdict)}`}
                    title={headline.verdict === 'Fail'
                      ? `Fails WCAG AA for ${headline.scope}, which needs ${WCAG_THRESHOLDS.small.aa}:1`
                      : `Meets WCAG ${headline.verdict} for ${headline.scope}`}
                  >
                    {headline.verdict}
                  </span>
                </div>
                <div className="stui-cp-pair muted">
                  <span
                    className="stui-cp-pairsample"
                    style={{ background: toRgbString(rgba), color: other.ink }}
                  >
                    Aa
                  </span>
                  <span className="stui-cp-alsotext">
                    {other.label} would be {other.ratio.toFixed(2)}:1
                    {' — '}{wcagVerdict(other.ratio, 'small')}
                  </span>
                </div>
              </div>
              <button
                type="button"
                className="stui-cp-icon-btn"
                onClick={() => setHelpOpen(o => !o)}
                title="What do these ratings mean?"
                aria-expanded={helpOpen}
              >
                <Info size={13} />
              </button>
            </div>

            {helpOpen && (
              <div className="stui-cp-help">
                <p className="stui-cp-helptext">
                  A contrast ratio is how far apart two colors are in brightness — <strong>1:1</strong> is
                  identical, <strong>21:1</strong> is black on white. WCAG 2.1 sets a different minimum for
                  each kind of content, so one ratio can pass for a heading and fail for body text.
                  Swapping the text and the background does not change it, and a translucent color is
                  flattened onto what is behind it first.
                </p>
                <table className="stui-cp-helptable">
                  <thead>
                    <tr>
                      <th>Content</th>
                      <th>AA needs</th>
                      <th>AAA needs</th>
                      {pairings.map(pair => <th key={pair.key}>{pair.label}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(WCAG_THRESHOLDS).map(([key, bar]) => (
                      <tr key={key}>
                        <td>{bar.label}</td>
                        <td>{bar.aa}:1</td>
                        <td>{bar.aaa ? `${bar.aaa}:1` : '—'}</td>
                        {pairings.map(pair => {
                          const verdict = wcagVerdict(pair.ratio, key);
                          return (
                            <td key={pair.key}>
                              <span className={`stui-badge ${badgeFor(verdict)}`}>{verdict}</span>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="stui-cp-helptext">
                  The badges above the table rate <strong>small text</strong> — the strictest case and the
                  one most likely to bite. A color that fails there can still be perfectly fine for a large
                  heading, a button border or an icon, which the table shows.
                </p>
              </div>
            )}
            <div className="stui-cp-statusrow">
              <span className="stui-cp-statuslabel">Return as</span>
              <StuiSelect className="stui-select stui-cp-return" value={outputMode} onChange={e => setOutputMode(e.target.value)}>
                {exactToken && <option value="token">var({exactToken})</option>}
                <option value="hex">{hexOut}</option>
                <option value="rgba">{toRgbString(rgba)}</option>
              </StuiSelect>
              <button type="button" className="stui-cp-icon-btn" onClick={copyOut} title="Copy value">
                {copied ? <Check size={12} /> : <Copy size={12} />}
              </button>
            </div>
          </div>

          {recents.length > 0 && (
            <div className="stui-cp-recents">
              <span className="stui-cp-statuslabel">Recent</span>
              {recents.map(entry => (
                <button
                  key={entry}
                  type="button"
                  className="stui-cp-recent stui-cp-checker"
                  title={entry}
                  onClick={() => {
                    const asToken = tokenFromValue(entry);
                    const raw = asToken ? resolveToken(asToken) : entry;
                    applyRgba(parseColor(raw));
                    setOutputMode(asToken ? 'token' : entry.startsWith('rgb') ? 'rgba' : 'hex');
                  }}
                >
                  <span style={{ background: entry }} />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="stui-modal-footer">
          <button type="button" className="stui-btn stui-btn-secondary" onClick={onClose}>Cancel</button>
          <button type="button" className="stui-btn stui-btn-primary" onClick={confirm}>OK</button>
        </div>
      </div>
    </div>
  );
};

/* ── Call-site field ──────────────────────────────────────────
   The standard trigger: a swatch plus the current value, opening
   the dialog. This is what replaces a bare <input type="color">.
─────────────────────────────────────────────────────────────── */

export const ColorPickerField = ({
  value = '',
  onChange,
  title = 'Colors',
  allowAlpha = true,
  disabled = false,
  placeholder = 'No color',
}) => {
  const [open, setOpen] = useState(false);
  const token = tokenFromValue(value);
  return (
    <>
      <button
        type="button"
        className="stui-cp-trigger"
        onClick={() => setOpen(true)}
        disabled={disabled}
        title={value || placeholder}
      >
        <span className="stui-cp-trigger-swatch stui-cp-checker">
          <span style={{ background: (token ? `var(${token})` : value) || 'transparent' }} />
        </span>
        <span className={`stui-cp-trigger-value${value ? '' : ' empty'}`}>{value || placeholder}</span>
      </button>
      <ColorPickerModal
        isOpen={open}
        title={title}
        value={value}
        allowAlpha={allowAlpha}
        onClose={() => setOpen(false)}
        onSelect={result => onChange?.(result.value, result)}
      />
    </>
  );
};

export default ColorPickerModal;
